/**
 * @file index.tsx
 *
 * @fileoverview Renders a facet for a multiselectable list. Receives a facet as prop. Will recursively
 * go through the items in the facet and set as active each that are represented in the selectedItems also
 * passed via props (These are calculated in the component that calls this one based on the url).
 */

import React, { SyntheticEvent, useState } from 'react';
import { Facet, FacetValue } from '~/jobiqo/models/search';
import { flattenForChildren } from '~/jobiqo/services/taxonomyHelpers';
import { Checkbox } from '~/vcl/atoms/01-forms/02-checkbox';
import { FacetChildList } from '~/vcl/molecules/07-search/02-facet/01-facet-box';
import {
  checkFacetExists,
  clearWithChildren,
  getAllChildren,
  getAllParents,
  getFacetLabel,
  getFacetMachineName,
} from '../utils/facetHelpers';

interface FacetMultipleProps {
  querystring: any;
  facet: Facet;
  selectedItems: string[];
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
}

interface FacetListProps {
  facetValues: FacetValue[];
  isChild?: boolean;
  facet: Facet;
  selectedItems: string[];
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void;
  selectedFacets: any;
  handleOptimisticUpdate: any;
}

/**
 * Method to help see if a filter is selected.
 */
const checkIfFacetExists = (
  filter: string,
  facet: Facet,
  selectedItems: string[],
): boolean => {
  return checkFacetExists(filter, facet.values, selectedItems);
};

/**
 * Return the facet label including counting.
 */
const getFacetLabelItem = (facetItem: FacetValue): string => {
  return getFacetLabel(facetItem);
};

/**
 * Handler for changing the element in the checkbox.
 *
 * Calls the prop given to it, called onFacetToggle. Whatever happens
 * inside that function is not the concern of this component. It simply calls
 * it with the new list of selected elements which is the purpose of this function.
 *
 * When an item is set to checked we need to make sure we set it and unset the
 * parents since we always want only the immediate parent selected. This is
 * becuase we otherwise have duplicated results because of OR is set in Solr. Imagine we
 * have a parent "General Business (8)" with two children "Engineering (5)" and "Medical
 * (1)" when we select "General Business (8)" we get the 8 items, but if we set then
 * "Engineering (5)" we expect only 5 to be set, but if we keep both items in selected items
 * we end up with all the 8 (General Business "OR" Engineering).
 *
 * When an item is unset we need to remove the filter from the selected items and add the
 * immediate parent (if any).
 *
 * @param {SyntheticEvent<HTMLInputElement>} event
 *   The event triggered by the checkbox.
 * @param {string} name
 *   The name of the facet.
 * @param {string} filter
 *   The filter we are changing (machine name of the facet item itself).
 */
const handleFacetChange = (
  event: SyntheticEvent<HTMLInputElement>,
  name: string,
  filter: string,
  facet: Facet,
  selectedItems: string[],
  onFacetToggle: (
    name: string,
    selectedItems: string[],
    alias?: string,
  ) => void,
): void => {
  const { checked } = event.currentTarget;
  // We always want only the deepest item selected. Parents will automatically be set when a child is set.
  const parents = getAllParents(filter, facet.values, [...facet.values]);

  // We need to get the alias for this filter.
  // Find the filter in the flatten list of values.
  const facetValue = flattenForChildren(facet.values).filter(
    (facetVal) => facetVal.filter === filter,
  );
  let alias = '';
  if (facetValue[0] && facetValue[0].alias) {
    alias = facetValue[0].alias;
  }

  if (checked) {
    const deepestFilter = [...selectedItems, filter].filter(
      (item) => !parents.includes(item),
    );

    if (alias) {
      onFacetToggle(name, deepestFilter, alias);
    } else {
      onFacetToggle(name, deepestFilter);
    }
  } else {
    let clearedItems = clearWithChildren(filter, facet, selectedItems);
    if (alias) {
      onFacetToggle(name, clearedItems, alias);
    } else {
      onFacetToggle(name, clearedItems);
    }
  }
};

const RecursiveCheckboxList = ({
  facetValues,
  isChild,
  facet,
  selectedItems,
  onFacetToggle,
  selectedFacets,
  handleOptimisticUpdate,
}: FacetListProps) => {
  function isFacetSelected(facetItem: FacetValue): boolean {
    return selectedFacets[facetItem.filter]
      ? selectedFacets[facetItem.filter]
      : checkIfFacetExists(facetItem.filter, facet, selectedItems);
  }

  return facetValues.map((facetItem) => {
    return (
      <FacetChildList key={facetItem.filter} isChild={isChild}>
        <Checkbox
          striked={facetItem.children?.some((child) => isFacetSelected(child))}
          checked={isFacetSelected(facetItem)}
          id={`${getFacetMachineName(facetItem.label)}`}
          label={getFacetLabelItem(facetItem)}
          onChange={(event) => {
            // Optimistically update this filter.
            handleOptimisticUpdate({
              ...selectedFacets,
              [facetItem.filter]: event.currentTarget.checked,
            });

            handleFacetChange(
              event,
              facet.name,
              facetItem.filter,
              facet,
              selectedItems,
              onFacetToggle,
            );
          }}
        />
        {facetItem.children &&
          RecursiveCheckboxList({
            facetValues: facetItem.children,
            isChild: true,
            facet,
            selectedItems,
            onFacetToggle,
            selectedFacets,
            handleOptimisticUpdate,
          })}
      </FacetChildList>
    );
  });
};

export const FacetMultiple = ({
  facet,
  selectedItems,
  onFacetToggle,
}: FacetMultipleProps): any => {
  const [selectedFacets, setSelectedFacets] = useState({});

  const handleOptimisticUpdate = (newFacets) => {
    // Find the facet being unselected.
    const uncheckedFacet = Object.keys(newFacets).filter((facetToClean) => {
      return newFacets[facetToClean] === false;
    })[0];

    // Find any children of the facet being removed.
    // If they are a parent we have to remove its children also.
    const allChildrenToRemove = getAllChildren(
      uncheckedFacet,
      facet.values,
    ).map((f) => f.filter);

    Object.keys(newFacets).map((newFacet) => {
      if (allChildrenToRemove.includes(newFacet)) {
        delete newFacets[newFacet];
      }
    });

    setSelectedFacets({ ...newFacets });
  };

  return RecursiveCheckboxList({
    facetValues: facet.values,
    isChild: true,
    facet,
    selectedItems,
    onFacetToggle,
    selectedFacets,
    handleOptimisticUpdate,
  });
};
export default FacetMultiple;
